import {
  CUSTOM_CART_TEMPLATE_TYPE,
  STATIC_CART_SECTION_COUNT,
  STATIC_CART_SECTION_INDEX,
  isStaticCartDrawerContentStep,
  isStaticCartDrawerTriggerStep,
} from "./custom-cart-drawer-schema.js";
import { POPUP_LAYOUT_IDS, isPopupLayout } from "./popup-layouts/index.js";
import { isStepHidden } from "./step-visibility.js";
import { PRODUCT_SHELL_LAYOUT } from "./product-shell-layout.js";
import {
  COVER_IMAGE_LAYOUT,
  DEFAULT_COVER_IMAGE_DISPLAY,
  DEFAULT_PRICE_DISPLAY,
  PRICE_LAYOUT,
} from "./product-block-display.js";
import {
  PRODUCT_UPSELL_PRIMARY_CTA_ACTION,
} from "./product-upsell-recommendation-defaults.js";
import { buildRewardBarSection } from "./reward-bar.js";

export {
  CUSTOM_CART_TEMPLATE_TYPE,
  STATIC_CART_SECTION_COUNT,
  STATIC_CART_SECTION_INDEX,
  isStaticCartDrawerContentStep,
  isStaticCartDrawerTriggerStep,
};

export const CUSTOM_CART_DRAWER_LAYOUT = {
  header: {
    cartTitle: "Your Cart",
    showCartTitle: true,
    showCartIcon: true,
    showItemCount: true,
    showItemsSubtitle: false,
    itemsSubtitleTemplate: "{count} items in your cart",
    bagIconBackground: "#f3f4f6",
    closeButtonBackground: "#f3f4f6",
    closeAriaLabel: "Close cart",
  },
  body: {
    emptyMessage: "Your cart is empty",
    showEmptyCartIcon: true,
    showContinueShopping: true,
    continueShoppingLabel: "Continue shopping",
    /** home | collections | close | custom */
    continueShoppingAction: "collections",
    continueShoppingUrl: "/collections/all",
    continueShoppingButtonBg: "#000000",
    continueShoppingButtonColor: "#ffffff",
    continueShoppingFontSize: 14,
    continueShoppingButtonRadius: 999,
    continueShoppingBorderWidth: 0,
    continueShoppingBorderColor: "#000000",
    /** Vertical / horizontal padding inside the empty-cart CTA. */
    continueShoppingPaddingY: 12,
    continueShoppingPaddingX: 28,
    /** Space above the empty-cart CTA. */
    continueShoppingMarginTop: 4,
    showRemoveButton: true,
    showQuantityControls: true,
    lineItemCard: false,
    lineItemBorderColor: "#eeeeee",
    lineItemBorderRadius: "8px",
    lineItemGap: "8px",
    savingsBadgeBackground: "#e6f4ea",
    savingsBadgeColor: "#2e7d32",
    variantSeparator: " · ",
    /** Tiny badge next to unavailable variants in cart/upsell dropdowns. */
    showVariantOutOfStockBadge: true,
    variantOutOfStockBadgeText: "Out of stock",
    variantOutOfStockBadgeColor: "#64748b",
    variantOutOfStockBadgeBackground: "#f8fafc",
    variantOutOfStockBadgeBorderColor: "#e2e8f0",
    discountCodeTitle: "Add discount code",
    discountCodePlaceholder: "Discount code",
    discountSaveLabel: "Save",
    orderNoteTitle: "Add order note",
    orderNotePlaceholder: "Leave a note for seller",
    orderNoteSaveLabel: "Save",
    /** New carts only — existing popups keep their saved section visibility. */
    showOrderNote: false,
  },
  footer: {
    subtotalLabel: "Estimated Total",
    checkoutLabel: "Secure Checkout",
    taxShippingNote: "Taxes and shipping calculated at checkout",
    checkoutShowLockIcon: true,
    checkoutShowSparkleIcon: true,
    /** Off until a merchant turns it on. Existing carts stay unchanged. */
    termsEnabled: false,
    termsPreselected: true,
    termsPrefix: "I agree to the",
    termsLinkLabel: "T&C",
    termsUrl: "",
    termsJoiner: "&",
    privacyLinkLabel: "Privacy Notice",
    privacyUrl: "",
    showSavingsBanner: true,
    savingsBannerBackground: "#000000",
    savingsBannerColor: "#ffffff",
    savingsBannerText: "🎉 You're saving {amount} 🥳 !",
    billSummaryTitle: "Bill Summary",
    billSubtotalLabel: "Total MRP",
    deliveryFeeLabel: "Delivery fee",
    deliveryFeeText: "To be calculated",
    mrpDiscountLabel: "Discount on MRP",
    couponDiscountLabel: "Coupon discount",
    grandTotalLabel: "Grand total",
    /** New carts only. Existing sections omit this and keep the previous bill. */
    billSummaryDetailLayout: true,
    billMetaLineSize: 12,
    sellingSubtotalLabel: "Subtotal",
    billSummaryTitleSize: 16,
    billSummaryTitleColor: "#111827",
    billSummaryLineSize: 14,
    billSummaryLineColor: "#111827",
    discountAmountColor: "#2e7d32",
  },
};

export const CUSTOM_CART_DRAWER_POPUP_SETTINGS = {
  zIndex: 2147483647,
  customCSS: {
    desktop: {
      width: 480,
      height: "100%",
      widthUnit: "px",
      heightUnit: "%",
      borderRadius: "20px",
      backgroundColor: "#ffffff",
    },
    mobile: {
      width: "100%",
      height: "100%",
      widthUnit: "%",
      heightUnit: "%",
      borderRadius: "20px",
      backgroundColor: "#ffffff",
    },
  },
    dynamicalOptions: {
    overlay_style: "Dim",
    isHideBgOverlay: false,
    fontFamily: "Inter",
    popupPosition: {
      desktop: "bottom right",
      mobile: "bottom right",
    },
    window: {
      padding: {
        desktop: 0,
        mobile: 0,
      },
    },
  },
};

function mergeLayout(overrides = {}) {
  const next = {
    header: { ...CUSTOM_CART_DRAWER_LAYOUT.header, ...(overrides.header || {}) },
    body: { ...CUSTOM_CART_DRAWER_LAYOUT.body, ...(overrides.body || {}) },
    footer: { ...CUSTOM_CART_DRAWER_LAYOUT.footer, ...(overrides.footer || {}) },
  };
  // Compact Design tab tokens (font, colors, radius, side margin, custom CSS).
  if (overrides.design && typeof overrides.design === "object") {
    next.design = { ...overrides.design };
  }
  return next;
}

/** http(s) only. A bare host is treated as https. */
export function safeCartDrawerTermsUrl(value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const candidate = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  try {
    const url = new URL(candidate);
    if (url.protocol !== "http:" && url.protocol !== "https:") return "";
    if (!url.hostname || !url.hostname.includes(".")) return "";
    return url.href;
  } catch {
    return "";
  }
}

function termsText(primary, fallback, fallbackText) {
  const fromPrimary = primary != null ? String(primary).trim() : "";
  if (fromPrimary) return fromPrimary;
  const fromFallback = fallback != null ? String(fallback).trim() : "";
  if (fromFallback) return fromFallback;
  return fallbackText;
}

/**
 * Terms row above checkout. Enabled only when the merchant turns it on.
 * Checkout setting wins over layout.footer when both are present.
 */
export function resolveCartDrawerTermsAgreement(footer = {}, setting = {}) {
  const defaults = CUSTOM_CART_DRAWER_LAYOUT.footer;
  const enabled = setting?.termsEnabled === true || footer?.termsEnabled === true;
  const preselected =
    setting?.termsPreselected != null
      ? setting.termsPreselected !== false
      : footer?.termsPreselected !== false;
  return {
    enabled,
    preselected,
    prefix: termsText(setting?.termsPrefix, footer?.termsPrefix, defaults.termsPrefix),
    termsLabel: termsText(
      setting?.termsLinkLabel,
      footer?.termsLinkLabel,
      defaults.termsLinkLabel
    ),
    termsUrl: safeCartDrawerTermsUrl(setting?.termsUrl || footer?.termsUrl),
    joiner: termsText(setting?.termsJoiner, footer?.termsJoiner, defaults.termsJoiner),
    privacyLabel:
      setting?.privacyLinkLabel != null
        ? String(setting.privacyLinkLabel).trim()
        : footer?.privacyLinkLabel != null
          ? String(footer.privacyLinkLabel).trim()
          : defaults.privacyLinkLabel,
    privacyUrl: safeCartDrawerTermsUrl(setting?.privacyUrl || footer?.privacyUrl),
  };
}

/** Storefront intercept defaults (preview bundle builder). */
const DEFAULT_PRODUCT_CONDITION = {
  onCartAdd: true,
  onCartAddEventPrevented: false,
  allowCustomerCartDrawer: true,
  oneTimeShow: false,
  allowTrigger: false,
  linkToProduct: true,
  linkToProductOpenInNewTab: true,
  offer_is_triggered: { type: "all_product", value: true },
  products_to_offer: { type: "all_product", value: [] },
};

function baseTextCss(overrides = {}) {
  return {
    desktop: {
      fontFamily: "Inter, Segoe UI, system-ui, sans-serif",
      fontSize: "20px",
      fontWeight: "700",
      color: "#000000",
      justifyContent: "flex-start",
      textAlign: "left",
      ...overrides.desktop,
    },
    mobile: {
      fontFamily: "Inter, Segoe UI, system-ui, sans-serif",
      fontSize: "18px",
      fontWeight: "700",
      color: "#000000",
      justifyContent: "flex-start",
      textAlign: "left",
      ...overrides.mobile,
    },
  };
}

function baseButtonCss(overrides = {}) {
  return {
    desktop: {
      fontFamily: "Inter, Segoe UI, system-ui, sans-serif",
      fontSize: "16px",
      fontWeight: "700",
      color: "#c5a370",
      backgroundColor: "#0a0e1a",
      backgroundImage: "linear-gradient(180deg, #121a26 0%, #0a0e1a 100%)",
      borderRadius: "10px",
      width: "100%",
      letterSpacing: "0.02em",
      ...overrides.desktop,
    },
    mobile: {
      fontFamily: "Inter, Segoe UI, system-ui, sans-serif",
      fontSize: "15px",
      fontWeight: "700",
      color: "#c5a370",
      backgroundColor: "#0a0e1a",
      backgroundImage: "linear-gradient(180deg, #121a26 0%, #0a0e1a 100%)",
      borderRadius: "10px",
      width: "100%",
      letterSpacing: "0.02em",
      ...overrides.mobile,
    },
  };
}

function footerDiscountLabelCss() {
  return baseTextCss({
    desktop: { fontSize: "14px", fontWeight: "400", color: "#1a1a1a" },
    mobile: { fontSize: "14px", fontWeight: "400", color: "#1a1a1a" },
  });
}

function footerSubtotalLabelCss() {
  return baseTextCss({
    desktop: { fontSize: "16px", fontWeight: "700", color: "#000000" },
    mobile: { fontSize: "15px", fontWeight: "700", color: "#000000" },
  });
}

function footerSubtotalPriceCss() {
  return baseTextCss({
    desktop: { fontSize: "16px", fontWeight: "700", color: "#000000" },
    mobile: { fontSize: "15px", fontWeight: "700", color: "#000000" },
  });
}

function footerTaxNoteCss() {
  return baseTextCss({
    desktop: {
      fontSize: "13px",
      fontWeight: "400",
      color: "#757575",
      marginBottom: "16px",
      marginBottomPx: 16,
    },
    mobile: {
      fontSize: "12px",
      fontWeight: "400",
      color: "#757575",
      marginBottom: "14px",
      marginBottomPx: 14,
    },
  });
}

function cartDrawerCollapsibleHeaderCss({ hidden = false } = {}) {
  const css = baseTextCss({
    desktop: {
      fontSize: "14px",
      fontWeight: "400",
      color: "#000000",
      textDecoration: "underline",
      backgroundColor: "transparent",
      border: "0",
      padding: "0",
    },
    mobile: {
      fontSize: "14px",
      fontWeight: "400",
      color: "#000000",
      textDecoration: "underline",
      backgroundColor: "transparent",
      border: "0",
      padding: "0",
    },
  });
  if (!hidden) return css;
  return {
    desktop: { ...(css.desktop || {}), display: "none" },
    mobile: { ...(css.mobile || {}), display: "none" },
  };
}

function cartDrawerSaveButtonCss() {
  return baseButtonCss({
    desktop: {
      backgroundColor: "#000000",
      backgroundImage: "none",
      color: "#ffffff",
      borderRadius: "999px",
      width: "auto",
      minWidth: "120px",
      padding: "12px 28px",
    },
    mobile: {
      backgroundColor: "#000000",
      backgroundImage: "none",
      color: "#ffffff",
      borderRadius: "999px",
      width: "auto",
      minWidth: "120px",
      padding: "12px 28px",
    },
  });
}

function announcementBarCss() {
  return baseTextCss({
    desktop: {
      fontSize: "12px",
      fontWeight: "600",
      color: "#ffffff",
      backgroundColor: "#4d4d4d",
      textAlign: "center",
      justifyContent: "center",
      borderRadius: "0px",
      borderWidth: 0,
      borderStyle: "none",
      borderColor: "#4d4d4d",
      padding: "0 12px",
      width: "100%",
      display: "flex",
      height: "28px",
      minHeight: "25px",
      maxHeight: "30px",
    },
    mobile: {
      fontSize: "12px",
      fontWeight: "600",
      color: "#ffffff",
      backgroundColor: "#4d4d4d",
      textAlign: "center",
      justifyContent: "center",
      borderRadius: "0px",
      borderWidth: 0,
      borderStyle: "none",
      borderColor: "#4d4d4d",
      padding: "0 12px",
      width: "100%",
      display: "flex",
      height: "28px",
      minHeight: "25px",
      maxHeight: "30px",
    },
  });
}

function headerDividerCss() {
  return {
    desktop: {
      backgroundColor: "rgba(15, 23, 42, 0.08)",
      width: 100,
      height: 1,
      widthUnit: "%",
      heightUnit: "px",
    },
    mobile: {
      backgroundColor: "rgba(15, 23, 42, 0.08)",
      width: 100,
      height: 1,
      widthUnit: "%",
      heightUnit: "px",
    },
  };
}

const CART_DRAWER_UPSELL_PRICE_HTML =
  '<div class="price-container"><span class="price">{{ currency }} {{ discount_price }}</span><span class="compare-price">{{ currency }} {{ price }}</span></div>';

const CART_DRAWER_UPSELL_PRICE_OVERRIDE = `.price-container{
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 6px;
  margin: 0;
  padding: 0;
}
.price {
  font-weight: 700;
  font-size: 13px;
  color: #111827;
  line-height: 1.2;
}
.compare-price {
  text-decoration: line-through;
  color: #6b7280;
  font-size: 12px;
  line-height: 1.2;
}`;

function extractPriceFontSizeFromOverride(overrideCss = "") {
  if (!overrideCss || typeof overrideCss !== "string") return null;
  const match = overrideCss.match(/\.price\s*\{[^}]*font-size:\s*([^;}\n]+)/i);
  return match?.[1]?.trim() || null;
}

function ensureCartDrawerUpsellPriceSubsection(section) {
  if (section?.type !== "PRICE") return section;

  const setting = section.setting || {};
  const customCSS = { ...(setting.customCSS || {}) };
  const overrideCss = setting.overrideCss || {};

  for (const viewport of ["desktop", "mobile"]) {
    const current = customCSS[viewport] || {};
    if (current.fontSize) {
      customCSS[viewport] = current;
      continue;
    }
    const fromOverride = extractPriceFontSizeFromOverride(overrideCss[viewport]);
    customCSS[viewport] = {
      ...current,
      fontSize: fromOverride || ("12px"),
    };
  }

  return {
    ...section,
    setting: {
      ...setting,
      customCSS,
    },
  };
}

function ensureCartDrawerUpsellProductSection(productSection) {
  if (!productSection || productSection.type !== "PRODUCT") {
    return productSection;
  }
  if (!Array.isArray(productSection.sections)) {
    return productSection;
  }
  return {
    ...productSection,
    sections: productSection.sections.map(ensureCartDrawerUpsellPriceSubsection),
  };
}

/** Carousel cards visible per row on product page upsell. */
export const CART_DRAWER_UPSELL_ITEMS_PER_ROW_MIN = 1;
export const CART_DRAWER_UPSELL_ITEMS_PER_ROW_MAX = 6;
export const CART_DRAWER_UPSELL_ITEMS_PER_ROW_DEFAULT = 3;

/** Cart drawer + product page upsell price block — compare-at on, save pill off by default. */
export const CART_DRAWER_UPSELL_PRICE_DISPLAY = {
  ...DEFAULT_PRICE_DISPLAY,
  showCompareAt: true,
  showSavePill: false,
};

export function clampCartDrawerUpsellItemsPerRow(
  value,
  fallback = CART_DRAWER_UPSELL_ITEMS_PER_ROW_DEFAULT
) {
  const raw = Number(value);
  if (!Number.isFinite(raw)) return fallback;
  return Math.min(
    CART_DRAWER_UPSELL_ITEMS_PER_ROW_MAX,
    Math.max(CART_DRAWER_UPSELL_ITEMS_PER_ROW_MIN, Math.round(raw))
  );
}

/** Horizontal row subsections: image | title+price | + Add */
export function buildCartDrawerHorizontalProductSubsections() {
  return [
    {
      type: "COVER_IMAGE",
      src: { image: "", imageNames: [], images: [] },
      backgroundOpacity: 1,
      objectFit: "contain",
      objectPosition: "center",
      setting: {
        display: {
          ...DEFAULT_COVER_IMAGE_DISPLAY,
          layout: COVER_IMAGE_LAYOUT.SINGLE,
          autoImageHeight: false,
          sizes: {
            desktop: { columnWidth: 72, mainHeight: 72, thumbWidth: 36, thumbHeight: 36 },
            mobile: { columnWidth: 64, mainHeight: 64, thumbWidth: 32, thumbHeight: 32 },
          },
        },
        customCSS: {
          desktop: { borderRadius: 8, objectFit: "contain", backgroundColor: "#f1f5f9" },
          mobile: { borderRadius: 8, objectFit: "contain", backgroundColor: "#f1f5f9" },
        },
        overrideCss: { desktop: "", mobile: "" },
      },
    },
    {
      type: "TITLE",
      text: "{{title}}",
      setting: {
        customCSS: {
          desktop: {
            fontSize: "14px",
            fontWeight: "600",
            lineHeight: "1.35",
            color: "#0f172a",
            textAlign: "left",
            justifyContent: "flex-start",
          },
          mobile: {
            fontSize: "13px",
            fontWeight: "600",
            lineHeight: "1.35",
            color: "#0f172a",
            textAlign: "left",
            justifyContent: "flex-start",
          },
        },
        overrideCss: { desktop: "", mobile: "" },
      },
      AIText: [],
    },
    {
      type: "PRICE",
      content: CART_DRAWER_UPSELL_PRICE_HTML,
      setting: {
        display: {
          ...CART_DRAWER_UPSELL_PRICE_DISPLAY,
          layout: PRICE_LAYOUT.INLINE_ROW,
        },
        overrideCss: {
          desktop: CART_DRAWER_UPSELL_PRICE_OVERRIDE,
          mobile: CART_DRAWER_UPSELL_PRICE_OVERRIDE,
        },
        customCSS: {
          desktop: {
            textAlign: "left",
            justifyContent: "flex-start",
            fontSize: "12px",
          },
          mobile: {
            textAlign: "left",
            justifyContent: "flex-start",
            fontSize: "12px",
          },
        },
      },
    },
    {
      type: "BUTTON",
      text: "+ Add",
      setting: {
        action: { ...PRODUCT_UPSELL_PRIMARY_CTA_ACTION },
        isConversationEnabled: true,
        btn_type: { type: "text" },
        customCSS: {
          desktop: {
            backgroundColor: "#0f172a",
            color: "#ffffff",
            borderRadius: 8,
            fontSize: "13px",
            fontWeight: "600",
            width: "auto",
            minWidth: "72px",
            paddingLeft: "14px",
            paddingRight: "14px",
          },
          mobile: {
            backgroundColor: "#0f172a",
            color: "#ffffff",
            borderRadius: 8,
            fontSize: "12px",
            fontWeight: "600",
            width: "auto",
            minWidth: "68px",
            paddingLeft: "12px",
            paddingRight: "12px",
          },
        },
        overrideCss: { desktop: "", mobile: "" },
      },
    },
  ];
}

const CART_DRAWER_CAROUSEL_PRICE_HTML =
  '<div class="price-container"><span class="price">{{ currency }} {{ discount_price }}</span></div>';

const CART_DRAWER_CAROUSEL_PRICE_OVERRIDE = `.price-container{
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: center;
  margin: 0;
  padding: 0;
  gap: 4px;
}
.price {
  font-weight: 700;
  font-size: 12px;
  color: #111827;
  line-height: 1.2;
}
.compare-price {
  font-weight: 500;
  font-size: 11px;
  color: #6b7280;
  line-height: 1.2;
  text-decoration: line-through;
}`;

/** Compact vertical cards for cart drawer carousel (image → title → price → CTA). */
export function buildCartDrawerVerticalProductSubsections() {
  const addButtonCss = {
    desktop: {
      backgroundColor: "#0a0e1a",
      color: "#ffffff",
      borderRadius: 8,
      fontSize: "11px",
      fontWeight: "700",
      width: "100%",
      minWidth: "0",
      paddingLeft: "10px",
      paddingRight: "10px",
    },
    mobile: {
      backgroundColor: "#0a0e1a",
      color: "#ffffff",
      borderRadius: 8,
      fontSize: "11px",
      fontWeight: "700",
      width: "100%",
      minWidth: "0",
      paddingLeft: "10px",
      paddingRight: "10px",
    },
  };

  return [
    {
      type: "COVER_IMAGE",
      src: { image: "", imageNames: [], images: [] },
      backgroundOpacity: 1,
      objectFit: "contain",
      objectPosition: "center",
      setting: {
        display: {
          ...DEFAULT_COVER_IMAGE_DISPLAY,
          layout: COVER_IMAGE_LAYOUT.SINGLE,
          autoImageHeight: false,
          sizes: {
            desktop: { columnWidth: 72, mainHeight: 72, thumbWidth: 36, thumbHeight: 36 },
            mobile: { columnWidth: 64, mainHeight: 64, thumbWidth: 32, thumbHeight: 32 },
          },
        },
        customCSS: {
          desktop: { borderRadius: 8, objectFit: "contain", backgroundColor: "#f8fafc" },
          mobile: { borderRadius: 8, objectFit: "contain", backgroundColor: "#f8fafc" },
        },
        overrideCss: { desktop: "", mobile: "" },
      },
    },
    {
      type: "TITLE",
      text: "{{title}}",
      setting: {
        customCSS: {
          desktop: {
            fontSize: "11px",
            fontWeight: "600",
            lineHeight: "1.25",
            color: "#111827",
            textAlign: "center",
            justifyContent: "center",
          },
          mobile: {
            fontSize: "11px",
            fontWeight: "600",
            lineHeight: "1.25",
            color: "#111827",
            textAlign: "center",
            justifyContent: "center",
          },
        },
        overrideCss: { desktop: "", mobile: "" },
      },
      AIText: [],
    },
    {
      type: "PRICE",
      content: CART_DRAWER_CAROUSEL_PRICE_HTML,
      setting: {
        display: {
          ...CART_DRAWER_UPSELL_PRICE_DISPLAY,
          layout: PRICE_LAYOUT.INLINE_ROW,
        },
        overrideCss: {
          desktop: CART_DRAWER_CAROUSEL_PRICE_OVERRIDE,
          mobile: CART_DRAWER_CAROUSEL_PRICE_OVERRIDE,
        },
        customCSS: {
          desktop: {
            textAlign: "center",
            justifyContent: "center",
            fontSize: "12px",
          },
          mobile: {
            textAlign: "center",
            justifyContent: "center",
            fontSize: "12px",
          },
        },
      },
    },
    {
      type: "BUTTON",
      text: "+ Add",
      setting: {
        role: "primaryCta",
        action: { ...PRODUCT_UPSELL_PRIMARY_CTA_ACTION },
        isConversationEnabled: true,
        btn_type: { type: "text" },
        customCSS: JSON.parse(JSON.stringify(addButtonCss)),
        overrideCss: { desktop: "", mobile: "" },
      },
    },
  ];
}

export function buildStaticCartUpsellHeadingSection(
  text = "You might also like"
) {
  return {
    type: "TITLE",
    text,
    setting: {
      cartDrawerPart: "upsell_heading",
      customCSS: baseTextCss({
        desktop: { fontSize: "15px", fontWeight: "700", color: "#0f172a" },
        mobile: { fontSize: "14px", fontWeight: "700", color: "#0f172a" },
      }),
      overrideCss: { desktop: "", mobile: "" },
    },
  };
}

export function buildStaticCartUpsellProductSection(
  shell = PRODUCT_SHELL_LAYOUT.CART_DRAWER_HORIZONTAL
) {
  const isVertical = shell === PRODUCT_SHELL_LAYOUT.CART_DRAWER_VERTICAL;
  return {
    type: "PRODUCT",
    setting: {
      cartDrawerPart: "upsell_product",
      showVariantSelector: false,
      layout: {
        shell,
        imagePosition: isVertical ? "top" : "left",
        density: isVertical ? "compact" : "standard",
      },
    },
    sections: isVertical
      ? buildCartDrawerVerticalProductSubsections()
      : buildCartDrawerHorizontalProductSubsections(),
    products: [],
  };
}

/** Editor layout picker for static cart drawer upsell PRODUCT block. */
export const CART_DRAWER_UPSELL_LAYOUT_OPTIONS = Object.freeze([
  {
    value: PRODUCT_SHELL_LAYOUT.CART_DRAWER_HORIZONTAL,
    label: "Horizontal row",
    description: "Image, details, and add button in one row",
    preview: "cart-upsell-horizontal",
  },
  {
    value: PRODUCT_SHELL_LAYOUT.CART_DRAWER_VERTICAL,
    label: "Carousel cards",
    description: "Vertical cards in a scrollable row",
    preview: "cart-upsell-carousel",
  },
]);

export function isCartDrawerUpsellVerticalShell(shell) {
  return shell === PRODUCT_SHELL_LAYOUT.CART_DRAWER_VERTICAL;
}

/** Swap subsection defaults when merchant changes upsell layout in editor. */
export function buildCartDrawerUpsellSubsectionsForShell(shell) {
  const normalized = shell === PRODUCT_SHELL_LAYOUT.HORIZONTAL
    ? PRODUCT_SHELL_LAYOUT.CART_DRAWER_HORIZONTAL
    : shell;
  return isCartDrawerUpsellVerticalShell(normalized)
    ? buildCartDrawerVerticalProductSubsections()
    : buildCartDrawerHorizontalProductSubsections();
}

export function buildCartDrawerUpsellLayoutFields(shell, existingLayout = {}) {
  const normalized = shell === PRODUCT_SHELL_LAYOUT.HORIZONTAL
    ? PRODUCT_SHELL_LAYOUT.CART_DRAWER_HORIZONTAL
    : shell;
  const isVertical = isCartDrawerUpsellVerticalShell(normalized);
  return {
    shell: normalized,
    imagePosition: isVertical ? "top" : "left",
    density: isVertical ? "compact" : "standard",
  };
}

/** Editor + preview sections bound to existing Text/Button/CartItemBlock accordions. */
export function buildStaticCartDrawerSections(layout = CUSTOM_CART_DRAWER_LAYOUT) {
  return [
    {
      type: "TITLE",
      text: layout.header?.cartTitle || "Your cart",
      setting: {
        customCSS: baseTextCss(),
        overrideCss: { desktop: "", mobile: "" },
      },
    },
    {
      type: "BUTTON",
      text: "",
      setting: {
        action: { type: "close_popup" },
        btn_type: { type: "icon" },
        customCSS: {
          desktop: {
            color: "#475569",
            backgroundColor: "transparent",
            fontSize: "22px",
          },
          mobile: {
            color: "#475569",
            backgroundColor: "transparent",
            fontSize: "20px",
          },
        },
        overrideCss: { desktop: "", mobile: "" },
      },
    },
    {
      type: "DIVIDER",
      setting: {
        cartDrawerPart: "header_divider",
        customCSS: headerDividerCss(),
        overrideCss: { desktop: "", mobile: "" },
        dimension: {
          desktop: { width: 380, height: 8 },
          mobile: { width: 320, height: 8 },
        },
      },
    },
    {
      type: "TITLE",
      text: "Free Shipping From $100",
      texts: [
        "Free Shipping From $100",
        "Secure Payment | Buy More, Save More",
      ],
      setting: {
        cartDrawerPart: "announcement_bar",
        announcementTexts: [
          "Free Shipping From $100",
          "Secure Payment | Buy More, Save More",
        ],
        announcementRotateMs: 3500,
        customCSS: announcementBarCss(),
        overrideCss: { desktop: "", mobile: "" },
      },
    },
    buildRewardBarSection(),
    {
      type: "CARTITEMBLOCK",
      setting: {
        hideDeleteButton: layout.body?.showRemoveButton === false,
        hideQuantityButton: layout.body?.showQuantityControls === false,
        showLineComparePrice: true,
        /** New carts only. Saved sections omit this and stay off. */
        showLineDiscountChip: true,
        showVariantSelector: false,
        savingsColor: "#2e7d32",
        savingsBadgeBackground: "#e6f4ea",
        lineItemCard: false,
        lineDividerColor: layout.body?.lineItemBorderColor || "#eeeeee",
        variantColor: "#757575",
        titleFontSize: "12px",
        variantFontSize: "12px",
        priceColor: "#000000",
        priceFontSize: "12px",
        comparePriceColor: "#757575",
        comparePriceFontSize: "12px",
        customCSS: {
          desktop: {
            fontFamily: "Inter, Segoe UI, system-ui, sans-serif",
            fontSize: "12px",
            fontWeight: "600",
            color: "#000000",
          },
          mobile: {
            fontFamily: "Inter, Segoe UI, system-ui, sans-serif",
            fontSize: "12px",
            fontWeight: "600",
            color: "#000000",
          },
        },
        overrideCss: { desktop: "", mobile: "" },
      },
    },
    buildStaticCartUpsellHeadingSection(
      layout.body?.upsellHeading || "You might also like"
    ),
    buildStaticCartUpsellProductSection(
      layout.body?.upsellShell || PRODUCT_SHELL_LAYOUT.HORIZONTAL
    ),
    {
      type: "CART_DISCOUNT_INPUT",
      text: layout.body?.discountCodeTitle || "Add discount code",
      setting: {
        cartDrawerPart: "discount_code_input",
        inputPlaceholder:
          layout.body?.discountCodePlaceholder || "Discount code",
        showCouponSuggestions: true,
        showAllCouponCodes: true,
        selectedCouponCodes: [],
        selectedCouponDetails: [],
        saveButtonLabel: layout.body?.discountSaveLabel || "Save",
        saveButtonCustomCSS: cartDrawerSaveButtonCss(),
        customCSS: cartDrawerCollapsibleHeaderCss(),
        overrideCss: { desktop: "", mobile: "" },
      },
    },
    {
      type: "CART_ORDER_NOTE",
      text: layout.body?.orderNoteTitle || "Add order note",
      setting: {
        cartDrawerPart: "order_note_input",
        inputPlaceholder:
          layout.body?.orderNotePlaceholder || "Leave a note for seller",
        saveButtonLabel: layout.body?.orderNoteSaveLabel || "Save",
        saveButtonCustomCSS: cartDrawerSaveButtonCss(),
        // Off by default for new cart widgets; existing stores keep saved CSS.
        customCSS: cartDrawerCollapsibleHeaderCss({
          hidden: layout.body?.showOrderNote !== true,
        }),
        overrideCss: { desktop: "", mobile: "" },
      },
    },
    {
      type: "TITLE",
      text: layout.footer?.discountLabel || "Discounts",
      setting: {
        cartDrawerPart: "discount_summary",
        customCSS: footerDiscountLabelCss(),
        overrideCss: { desktop: "", mobile: "" },
        showSavingsBanner: layout.footer?.showSavingsBanner !== false,
        savingsBannerBackground:
          layout.footer?.savingsBannerBackground || "#000000",
        savingsBannerColor: layout.footer?.savingsBannerColor || "#ffffff",
        savingsBannerText:
          layout.footer?.savingsBannerText ||
          "🎉 You're saving {amount} 🥳 !",
        billSummaryTitle: layout.footer?.billSummaryTitle || "Bill Summary",
        billSubtotalLabel:
          layout.footer?.billSubtotalLabel || "Total MRP",
        deliveryFeeLabel: layout.footer?.deliveryFeeLabel || "Delivery fee",
        deliveryFeeText:
          layout.footer?.deliveryFeeText || "To be calculated",
        mrpDiscountLabel: layout.footer?.mrpDiscountLabel || "Discount on MRP",
        couponDiscountLabel:
          layout.footer?.couponDiscountLabel || "Coupon discount",
        grandTotalLabel: layout.footer?.grandTotalLabel || "Grand total",
        billSummaryDetailLayout: layout.footer?.billSummaryDetailLayout === true,
        billMetaLineSize: Number(layout.footer?.billMetaLineSize) || 12,
        sellingSubtotalLabel:
          layout.footer?.sellingSubtotalLabel || "Subtotal",
        billSummaryTitleSize: layout.footer?.billSummaryTitleSize || 16,
        billSummaryTitleColor:
          layout.footer?.billSummaryTitleColor || "#111827",
        billSummaryLineSize: layout.footer?.billSummaryLineSize || 14,
        billSummaryLineColor: layout.footer?.billSummaryLineColor || "#111827",
        discountAmountColor: layout.footer?.discountAmountColor || "#2e7d32",
      },
    },
    {
      type: "TITLE",
      text: layout.footer?.subtotalLabel || "Estimated Total",
      setting: {
        cartDrawerPart: "subtotal_label",
        customCSS: footerSubtotalLabelCss(),
        overrideCss: { desktop: "", mobile: "" },
      },
    },
    {
      type: "TITLE",
      text: "",
      setting: {
        cartDrawerPart: "subtotal_price",
        customCSS: footerSubtotalPriceCss(),
        overrideCss: { desktop: "", mobile: "" },
      },
    },
    {
      type: "TITLE",
      text: layout.footer?.taxShippingNote || "Taxes and shipping calculated at checkout",
      setting: {
        cartDrawerPart: "tax_note",
        customCSS: footerTaxNoteCss(),
        overrideCss: { desktop: "", mobile: "" },
      },
    },
    {
      type: "BUTTON",
      text: layout.footer?.checkoutLabel || "Secure Checkout",
      setting: {
        cartDrawerPart: "checkout",
        action: {
          type: "checkout",
          parameters: { checkoutAction: "checkout" },
        },
        btn_type: { type: "text" },
        showLockIcon: true,
        showSparkleIcon: true,
        checkoutLeftIcon: { image: "", images: [], imageNames: [] },
        checkoutRightIcon: { image: "", images: [], imageNames: [] },
        customCSS: baseButtonCss(),
        overrideCss: { desktop: "", mobile: "" },
      },
    },
  ];
}

function cloneValue(value) {
  if (value == null) return value;
  if (typeof structuredClone === "function") {
    return structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value));
}

function mergeViewportCss(defaultCss = {}, apiCss = {}) {
  return {
    desktop: { ...(defaultCss.desktop || {}), ...(apiCss.desktop || {}) },
    mobile: { ...(defaultCss.mobile || {}), ...(apiCss.mobile || {}) },
  };
}

/** Merge API section with code defaults — API wins for text/CSS user already set. */
function mergeStaticCartSection(apiSection, defaultSection) {
  if (!defaultSection) return apiSection;
  if (!apiSection || typeof apiSection !== "object") {
    return cloneValue(defaultSection);
  }

  const apiSetting = apiSection.setting || {};
  const defaultSetting = defaultSection.setting || {};
  const isPricePart = defaultSetting.cartDrawerPart === "subtotal_price";

  const text = isPricePart
    ? defaultSection.text ?? ""
    : apiSection.text != null && String(apiSection.text).trim() !== ""
      ? apiSection.text
      : defaultSection.text;

  return {
    ...defaultSection,
    ...apiSection,
    type: apiSection.type || defaultSection.type,
    text,
    setting: {
      ...defaultSetting,
      ...apiSetting,
      cartDrawerPart: apiSetting.cartDrawerPart || defaultSetting.cartDrawerPart,
      customCSS: mergeViewportCss(defaultSetting.customCSS, apiSetting.customCSS),
      overrideCss: {
        desktop:
          apiSetting.overrideCss?.desktop ??
          defaultSetting.overrideCss?.desktop ??
          "",
        mobile:
          apiSetting.overrideCss?.mobile ??
          defaultSetting.overrideCss?.mobile ??
          "",
      },
      action: (() => {
        const merged = {
          ...(defaultSetting.action || {}),
          ...(apiSetting.action || {}),
          parameters: {
            ...(defaultSetting.action?.parameters || {}),
            ...(apiSetting.action?.parameters || {}),
          },
        };
        if (defaultSetting.action?.type === "checkout") {
          merged.type = "checkout";
        }
        return merged;
      })(),
      btn_type: apiSetting.btn_type || defaultSetting.btn_type,
      hideDeleteButton:
        apiSetting.hideDeleteButton ?? defaultSetting.hideDeleteButton,
      hideQuantityButton:
        apiSetting.hideQuantityButton ?? defaultSetting.hideQuantityButton,
      showLineComparePrice:
        apiSetting.showLineComparePrice ?? defaultSetting.showLineComparePrice,
      showLineDiscountChip: apiSetting.showLineDiscountChip === true,
      showVariantSelector:
        apiSetting.showVariantSelector ?? defaultSetting.showVariantSelector,
      savingsColor:
        apiSetting.savingsColor ?? defaultSetting.savingsColor,
      lineDividerColor: (() => {
        const fromApi =
          apiSetting.lineDividerColor ?? apiSetting.lineItemBorderColor;
        if (fromApi != null && String(fromApi).trim() !== "") return fromApi;
        return defaultSetting.lineDividerColor;
      })(),
      variantColor: apiSetting.variantColor ?? defaultSetting.variantColor,
      titleFontSize: apiSetting.titleFontSize ?? defaultSetting.titleFontSize,
      variantFontSize:
        apiSetting.variantFontSize ?? defaultSetting.variantFontSize,
      priceColor: apiSetting.priceColor ?? defaultSetting.priceColor,
      priceFontSize: apiSetting.priceFontSize ?? defaultSetting.priceFontSize,
      comparePriceColor:
        apiSetting.comparePriceColor ?? defaultSetting.comparePriceColor,
      comparePriceFontSize:
        apiSetting.comparePriceFontSize ?? defaultSetting.comparePriceFontSize,
      showLockIcon: apiSetting.showLockIcon ?? defaultSetting.showLockIcon,
      showSparkleIcon:
        apiSetting.showSparkleIcon ?? defaultSetting.showSparkleIcon,
      checkoutLeftIcon: {
        ...(defaultSetting.checkoutLeftIcon || {
          image: "",
          images: [],
          imageNames: [],
        }),
        ...(apiSetting.checkoutLeftIcon || {}),
      },
      checkoutRightIcon: {
        ...(defaultSetting.checkoutRightIcon || {
          image: "",
          images: [],
          imageNames: [],
        }),
        ...(apiSetting.checkoutRightIcon || {}),
      },
      dimension: {
        desktop: {
          ...(defaultSetting.dimension?.desktop || {}),
          ...(apiSetting.dimension?.desktop || {}),
        },
        mobile: {
          ...(defaultSetting.dimension?.mobile || {}),
          ...(apiSetting.dimension?.mobile || {}),
        },
      },
    },
    ...(Array.isArray(apiSection.products) ? { products: apiSection.products } : {}),
    ...(Array.isArray(apiSection.sections) && apiSection.sections.length
      ? { sections: apiSection.sections }
      : {}),
  };
}

/** Pull known static-cart blocks out of any legacy section array shape. */
function extractStaticCartParts(sections) {
  const parts = {
    title: null,
    close: null,
    divider: null,
    announcementBar: null,
    rewardBar: null,
    body: null,
    upsellHeading: null,
    upsellProduct: null,
    subtotalLabel: null,
    subtotalPrice: null,
    taxNote: null,
    checkout: null,
    discountCodeInput: null,
    orderNote: null,
  };

  if (!Array.isArray(sections)) {
    return parts;
  }

  sections.forEach((section) => {
    if (!section?.type) return;
    const part = section?.setting?.cartDrawerPart;

    if (part === "announcement_bar") {
      parts.announcementBar = section;
      return;
    }
    if (section.type === "REWARD_BAR" || part === "reward_bar") {
      parts.rewardBar = section;
      return;
    }
    if (section.type === "CARTITEMBLOCK") {
      parts.body = section;
      return;
    }
    if (section.type === "PRODUCT") {
      parts.upsellProduct = section;
      return;
    }
    if (section.type === "DIVIDER") {
      parts.divider = section;
      return;
    }
    if (
      section.type === "BUTTON" &&
      section.setting?.action?.type === "close_popup"
    ) {
      parts.close = section;
      return;
    }
    if (
      section.type === "BUTTON" &&
      section.setting?.action?.type === "checkout"
    ) {
      parts.checkout = section;
      return;
    }
    if (part === "discount_summary") {
      parts.discount = section;
      return;
    }
    if (part === "discount_code_input" || section.type === "CART_DISCOUNT_INPUT") {
      parts.discountCodeInput = section;
      return;
    }
    if (part === "order_note_input" || section.type === "CART_ORDER_NOTE") {
      parts.orderNote = section;
      return;
    }
    if (part === "subtotal_label") {
      parts.subtotalLabel = section;
      return;
    }
    if (part === "subtotal_price") {
      parts.subtotalPrice = section;
      return;
    }
    if (part === "tax_note") {
      parts.taxNote = section;
      return;
    }
    if (part === "upsell_heading") {
      parts.upsellHeading = section;
      return;
    }
    if (part === "upsell_product") {
      parts.upsellProduct = section;
      return;
    }
    if (section.type === "TITLE" && !part && !parts.title) {
      parts.title = section;
    }
  });

  if (!parts.title && sections[0]?.type === "TITLE") {
    parts.title = sections[0];
  }
  if (!parts.close && sections[1]?.type === "BUTTON") {
    parts.close = sections[1];
  }
  if (!parts.divider && sections[2]?.type === "DIVIDER") {
    parts.divider = sections[2];
  }
  if (!parts.body) {
    const bodyAt =
      sections[2]?.type === "CARTITEMBLOCK"
        ? 2
        : sections[3]?.type === "CARTITEMBLOCK"
          ? 3
          : -1;
    if (bodyAt >= 0) parts.body = sections[bodyAt];
  }
  if (!parts.checkout) {
    const checkout = sections.find(
      (s) =>
        s?.type === "BUTTON" && s?.setting?.action?.type === "checkout"
    );
    if (checkout) parts.checkout = checkout;
  }

  return parts;
}

function remapStaticCartSelectedPart(part, rawSectionCount) {
  if (part == null || part < 0) return part;
  if (rawSectionCount >= STATIC_CART_SECTION_COUNT) return part;
  // Legacy 14-section drawer (no announcement bar): shift at/after old REWARD_BAR (3).
  if (rawSectionCount === 14 && part >= 3) {
    return part + 1;
  }
  // Legacy 12-section drawer (no discount code / order note blocks): shift footer indices.
  if (rawSectionCount === 12) {
    if (part >= 7) return part + 3;
    if (part >= 3) return part + 1;
    return part;
  }
  // Legacy 11-section drawer (no discount block): shift footer indices at/after old subtotal label (7).
  if (rawSectionCount === 11) {
    if (part >= 7) return part + 2;
    if (part >= 3) return part + 1;
    return part;
  }
  // Legacy 10-section drawer (no reward / announcement): shift at/after old BODY slot (3).
  if (rawSectionCount === 10 && part >= 3) {
    return part + 2;
  }
  if (rawSectionCount === 8 && part >= 4) {
    return part + 3;
  }
  if (rawSectionCount <= 4) {
    if (part === 3) return STATIC_CART_SECTION_INDEX.FOOTER_CHECKOUT;
    if (part >= 2) return part + 1;
    return part;
  }
  if (rawSectionCount === 7 && part >= 2) {
    return part + 1;
  }
  return part;
}

/** Upgrade legacy API schemas — append divider + footer blocks from code defaults. */
export function migrateStaticCartDrawerSections(
  sections,
  layout = CUSTOM_CART_DRAWER_LAYOUT
) {
  const mergedLayout = mergeLayout(layout);
  const defaults = buildStaticCartDrawerSections(mergedLayout);
  const parts = extractStaticCartParts(sections);

  const subtotalLabel = {
    ...defaults[STATIC_CART_SECTION_INDEX.FOOTER_SUBTOTAL_LABEL],
    ...(parts.subtotalLabel || {}),
    text:
      parts.subtotalLabel?.text ||
      mergedLayout.footer?.subtotalLabel ||
      defaults[STATIC_CART_SECTION_INDEX.FOOTER_SUBTOTAL_LABEL].text,
  };
  const taxNote = {
    ...defaults[STATIC_CART_SECTION_INDEX.FOOTER_TAX_NOTE],
    ...(parts.taxNote || {}),
    text:
      parts.taxNote?.text ||
      mergedLayout.footer?.taxShippingNote ||
      defaults[STATIC_CART_SECTION_INDEX.FOOTER_TAX_NOTE].text,
  };

  return [
    parts.title ?? defaults[STATIC_CART_SECTION_INDEX.HEADER_TITLE],
    parts.close ?? defaults[STATIC_CART_SECTION_INDEX.HEADER_CLOSE],
    parts.divider ?? defaults[STATIC_CART_SECTION_INDEX.HEADER_DIVIDER],
    parts.announcementBar ??
      (() => {
        const bar = defaults[STATIC_CART_SECTION_INDEX.ANNOUNCEMENT_BAR];
        return {
          ...bar,
          setting: {
            ...bar.setting,
            customCSS: {
              desktop: {
                ...(bar.setting?.customCSS?.desktop || {}),
                display: "none",
              },
              mobile: {
                ...(bar.setting?.customCSS?.mobile || {}),
                display: "none",
              },
            },
          },
        };
      })(),
    parts.rewardBar ??
      (() => {
        const bar = defaults[STATIC_CART_SECTION_INDEX.REWARD_BAR];
        return {
          ...bar,
          setting: {
            ...bar.setting,
            customCSS: {
              desktop: {
                ...(bar.setting?.customCSS?.desktop || {}),
                display: "none",
              },
              mobile: {
                ...(bar.setting?.customCSS?.mobile || {}),
                display: "none",
              },
            },
          },
        };
      })(),
    (() => {
      const body = parts.body ?? defaults[STATIC_CART_SECTION_INDEX.BODY];
      const borderColor =
        String(body?.setting?.lineDividerColor || "").trim() ||
        String(body?.setting?.lineItemBorderColor || "").trim() ||
        String(mergedLayout.body?.lineItemBorderColor || "").trim() ||
        defaults[STATIC_CART_SECTION_INDEX.BODY].setting.lineDividerColor;
      return {
        ...defaults[STATIC_CART_SECTION_INDEX.BODY],
        ...body,
        setting: {
          ...defaults[STATIC_CART_SECTION_INDEX.BODY].setting,
          ...body.setting,
          lineDividerColor: borderColor,
          showLineDiscountChip: parts.body
            ? parts.body.setting?.showLineDiscountChip === true
            : defaults[STATIC_CART_SECTION_INDEX.BODY].setting
                .showLineDiscountChip === true,
        },
      };
    })(),
    parts.upsellHeading ??
      defaults[STATIC_CART_SECTION_INDEX.UPSELL_HEADING],
    ensureCartDrawerUpsellProductSection(
      parts.upsellProduct ?? defaults[STATIC_CART_SECTION_INDEX.UPSELL_PRODUCT]
    ),
    {
      ...defaults[STATIC_CART_SECTION_INDEX.DISCOUNT_CODE_INPUT],
      ...(parts.discountCodeInput || {}),
      text:
        parts.discountCodeInput?.text ||
        mergedLayout.body?.discountCodeTitle ||
        defaults[STATIC_CART_SECTION_INDEX.DISCOUNT_CODE_INPUT].text,
      setting: {
        ...defaults[STATIC_CART_SECTION_INDEX.DISCOUNT_CODE_INPUT].setting,
        ...(parts.discountCodeInput?.setting || {}),
        inputPlaceholder:
          parts.discountCodeInput?.setting?.inputPlaceholder ||
          mergedLayout.body?.discountCodePlaceholder ||
          defaults[STATIC_CART_SECTION_INDEX.DISCOUNT_CODE_INPUT].setting
            .inputPlaceholder,
        saveButtonLabel:
          parts.discountCodeInput?.setting?.saveButtonLabel ||
          mergedLayout.body?.discountSaveLabel ||
          defaults[STATIC_CART_SECTION_INDEX.DISCOUNT_CODE_INPUT].setting
            .saveButtonLabel,
        saveButtonCustomCSS: {
          desktop: {
            ...(defaults[STATIC_CART_SECTION_INDEX.DISCOUNT_CODE_INPUT].setting
              .saveButtonCustomCSS?.desktop || {}),
            ...(parts.discountCodeInput?.setting?.saveButtonCustomCSS?.desktop ||
              {}),
          },
          mobile: {
            ...(defaults[STATIC_CART_SECTION_INDEX.DISCOUNT_CODE_INPUT].setting
              .saveButtonCustomCSS?.mobile || {}),
            ...(parts.discountCodeInput?.setting?.saveButtonCustomCSS?.mobile ||
              {}),
          },
        },
      },
    },
    {
      ...defaults[STATIC_CART_SECTION_INDEX.ORDER_NOTE],
      ...(parts.orderNote || {}),
      text:
        parts.orderNote?.text ||
        mergedLayout.body?.orderNoteTitle ||
        defaults[STATIC_CART_SECTION_INDEX.ORDER_NOTE].text,
      setting: {
        ...defaults[STATIC_CART_SECTION_INDEX.ORDER_NOTE].setting,
        ...(parts.orderNote?.setting || {}),
        inputPlaceholder:
          parts.orderNote?.setting?.inputPlaceholder ||
          mergedLayout.body?.orderNotePlaceholder ||
          defaults[STATIC_CART_SECTION_INDEX.ORDER_NOTE].setting.inputPlaceholder,
        saveButtonLabel:
          parts.orderNote?.setting?.saveButtonLabel ||
          mergedLayout.body?.orderNoteSaveLabel ||
          defaults[STATIC_CART_SECTION_INDEX.ORDER_NOTE].setting.saveButtonLabel,
        saveButtonCustomCSS: {
          desktop: {
            ...(defaults[STATIC_CART_SECTION_INDEX.ORDER_NOTE].setting
              .saveButtonCustomCSS?.desktop || {}),
            ...(parts.orderNote?.setting?.saveButtonCustomCSS?.desktop || {}),
          },
          mobile: {
            ...(defaults[STATIC_CART_SECTION_INDEX.ORDER_NOTE].setting
              .saveButtonCustomCSS?.mobile || {}),
            ...(parts.orderNote?.setting?.saveButtonCustomCSS?.mobile || {}),
          },
        },
        // Preserve existing carts' order-note visibility. Only brand-new /
        // newly-inserted sections use the hidden default from `defaults`.
        customCSS: parts.orderNote?.setting?.customCSS
          ? parts.orderNote.setting.customCSS
          : parts.orderNote
            ? cartDrawerCollapsibleHeaderCss()
            : defaults[STATIC_CART_SECTION_INDEX.ORDER_NOTE].setting.customCSS,
      },
    },
    {
      ...defaults[STATIC_CART_SECTION_INDEX.FOOTER_DISCOUNT],
      ...(parts.discount || {}),
      text:
        parts.discount?.text ||
        mergedLayout.footer?.discountLabel ||
        defaults[STATIC_CART_SECTION_INDEX.FOOTER_DISCOUNT].text,
    },
    subtotalLabel,
    parts.subtotalPrice ??
      defaults[STATIC_CART_SECTION_INDEX.FOOTER_SUBTOTAL_PRICE],
    taxNote,
    (() => {
      const rawCheckoutText = String(parts.checkout?.text || "").trim();
      const layoutCheckoutLabel = String(
        mergedLayout.footer?.checkoutLabel || ""
      ).trim();
      let checkoutText =
        rawCheckoutText ||
        layoutCheckoutLabel ||
        defaults[STATIC_CART_SECTION_INDEX.FOOTER_CHECKOUT].text;
      if (
        rawCheckoutText.toLowerCase() === "checkout" &&
        layoutCheckoutLabel &&
        layoutCheckoutLabel.toLowerCase() !== "checkout"
      ) {
        checkoutText = layoutCheckoutLabel;
      }
      return {
        ...defaults[STATIC_CART_SECTION_INDEX.FOOTER_CHECKOUT],
        ...(parts.checkout || {}),
        text: checkoutText,
        setting: {
          ...defaults[STATIC_CART_SECTION_INDEX.FOOTER_CHECKOUT].setting,
          ...(parts.checkout?.setting || {}),
          action: {
            type: "checkout",
            parameters: {
              ...(defaults[STATIC_CART_SECTION_INDEX.FOOTER_CHECKOUT].setting
                ?.action?.parameters || {}),
              ...(parts.checkout?.setting?.action?.parameters || {}),
            },
          },
        },
      };
    })(),
  ];
}

/**
 * Merge API/static-cart step with in-code defaults (no DB migration required).
 */
function mergePopupSettings(stepSettings = {}) {
  const baseWindow = CUSTOM_CART_DRAWER_POPUP_SETTINGS.dynamicalOptions?.window || {};
  const stepWindow = stepSettings.dynamicalOptions?.window || {};
  const stepDesktop = stepSettings.customCSS?.desktop || {};
  const legacyWidth = Number(stepDesktop.width);
  const desktopWidth =
    legacyWidth === 420
      ? CUSTOM_CART_DRAWER_POPUP_SETTINGS.customCSS.desktop.width
      : stepDesktop.width;

  return {
    ...CUSTOM_CART_DRAWER_POPUP_SETTINGS,
    ...stepSettings,
    zIndex: stepSettings.zIndex ?? CUSTOM_CART_DRAWER_POPUP_SETTINGS.zIndex,
    customCSS: {
      desktop: {
        ...CUSTOM_CART_DRAWER_POPUP_SETTINGS.customCSS.desktop,
        ...stepDesktop,
        ...(desktopWidth != null ? { width: desktopWidth } : null),
      },
      mobile: {
        ...CUSTOM_CART_DRAWER_POPUP_SETTINGS.customCSS.mobile,
        ...(stepSettings.customCSS?.mobile || {}),
      },
    },
    dynamicalOptions: {
      ...CUSTOM_CART_DRAWER_POPUP_SETTINGS.dynamicalOptions,
      ...(stepSettings.dynamicalOptions || {}),
      popupPosition: {
        ...CUSTOM_CART_DRAWER_POPUP_SETTINGS.dynamicalOptions.popupPosition,
        ...(stepSettings.dynamicalOptions?.popupPosition || {}),
      },
      window: {
        ...baseWindow,
        ...stepWindow,
        padding: {
          ...baseWindow.padding,
          ...(stepWindow.padding || {}),
        },
      },
    },
  };
}

/** Sync layout + popupSettings from persisted sections (no code-default merge). */
export function syncStaticCartDrawerSchemaStep(step) {
  if (!step || typeof step !== "object") {
    return step;
  }

  const layout = mergeLayout(step.cartDrawerLayout);
  const rawSection = Array.isArray(step.section) ? step.section : null;
  const rawSections = Array.isArray(step.sections) ? step.sections : null;
  let raw = rawSection ?? rawSections ?? [];
  if (rawSection && rawSections && rawSection !== rawSections) {
    const hasReadyProducts = (list) =>
      list.some(
        (sec) =>
          sec?.type === "PRODUCT" &&
          Array.isArray(sec.products) &&
          sec.products.length > 0
      );
    if (!hasReadyProducts(rawSection) && hasReadyProducts(rawSections)) {
      raw = rawSections;
    } else {
      raw = rawSection;
    }
  }
  const sections = migrateStaticCartDrawerSections(raw, layout);
  const cartDrawerLayout = syncCartDrawerLayoutFromSections({
    ...step,
    section: sections,
    cartDrawerLayout: layout,
  });

  return {
    ...step,
    cartDrawerLayout,
    popupSettings: {
      ...mergePopupSettings(step.popupSettings),
      step_name:
        step.popupSettings?.step_name === "Book" || !step.popupSettings?.step_name
          ? "Drawer"
          : step.popupSettings.step_name,
    },
    section: sections,
  };
}

/** Template builder / legacy migration — merges code defaults with partial sections. */
export function ensureStaticCartDrawerSchemaStep(step) {
  if (!step || typeof step !== "object") {
    return {
      section: buildStaticCartDrawerSections(),
      cartDrawerLayout: CUSTOM_CART_DRAWER_LAYOUT,
      popupSettings: mergePopupSettings(),
    };
  }

  const layout = mergeLayout(step.cartDrawerLayout);
  const defaults = buildStaticCartDrawerSections(layout);
  const raw = step.section ?? step.sections ?? [];
  const migrated = migrateStaticCartDrawerSections(raw, layout);
  const sectionCount = Math.max(
    STATIC_CART_SECTION_COUNT,
    defaults.length,
    migrated.length
  );
  const sections = Array.from({ length: sectionCount }, (_, index) =>
    mergeStaticCartSection(
      migrated[index],
      defaults[index] || migrated[index] || null
    )
  ).slice(0, STATIC_CART_SECTION_COUNT);
  const cartDrawerLayout = syncCartDrawerLayoutFromSections({
    ...step,
    section: sections,
    cartDrawerLayout: layout,
  });

  return {
    ...step,
    cartDrawerLayout,
    popupSettings: {
      ...mergePopupSettings(step.popupSettings),
      step_name:
        step.popupSettings?.step_name === "Book" || !step.popupSettings?.step_name
          ? "Drawer"
          : step.popupSettings.step_name,
    },
    section: sections,
  };
}

const DEFAULT_CART_TRIGGER_ICON =
  "https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-product-1_large.png";

/** Floating cart icon step (opens drawer via go_to_step). */
export function buildDefaultCartTriggerStep(drawerStepIndex = 1) {
  const buttonSection = {
    type: "BUTTON",
    text: "",
    setting: {
      customCSS: {
        desktop: {
          fontWeight: "700",
          fontFamily: "Segoe UI",
          fontSize: "16px",
          color: "#ffffff",
          backgroundColor: "transparent",
          borderRadius: 5,
          justifyContent: "center",
          alignItems: "center",
          display: "flex",
          iconWidth: 28,
          iconHeight: 28,
          iconWidthUnit: "px",
          iconHeightUnit: "px",
        },
        mobile: {
          fontWeight: "700",
          fontFamily: "Segoe UI",
          fontSize: "16px",
          color: "#ffffff",
          backgroundColor: "transparent",
          borderRadius: 5,
          justifyContent: "center",
          alignItems: "center",
          display: "flex",
          iconWidth: 24,
          iconHeight: 24,
          iconWidthUnit: "px",
          iconHeightUnit: "px",
        },
      },
      dimension: {
        desktop: { width: 37, height: 38 },
        mobile: { width: 33, height: 33 },
      },
      position: {
        displacement: {
          desktop: { x: 12, y: 9 },
          mobile: { x: 8, y: 7 },
        },
      },
      overrideCss: { desktop: "", mobile: "" },
      action: {
        type: "go_to_step",
        parameters: {
          link: "",
          event: "",
          openStep: drawerStepIndex,
        },
      },
      btn_type: {
        type: "icon",
        icon: {
          image: DEFAULT_CART_TRIGGER_ICON,
          images: [DEFAULT_CART_TRIGGER_ICON],
          imageNames: ["cart_icon.png"],
        },
      },
    },
  };

  return {
    popupSettings: {
      customCSS: {
        position: "absolute",
        desktop: {
          opacity: 1,
          backgroundColor: "#2d2d2d",
          borderStyle: "solid",
          borderColor: "#000000",
          borderWidth: 0,
          borderRadius: 100,
          width: 60,
          height: 60,
          widthUnit: "px",
          heightUnit: "px",
          showBackground: true,
        },
        mobile: {
          opacity: 1,
          backgroundColor: "#007bff",
          borderStyle: "solid",
          borderColor: "#000000",
          borderWidth: 0,
          borderRadius: 93,
          width: 50,
          height: 50,
          widthUnit: "px",
          heightUnit: "px",
          showBackground: true,
        },
      },
      dynamicalOptions: {
        overlay_style: "None",
        isClickable: false,
        animation: "Fade In Right",
        attentionAnimation: "Float Top",
        fontFamily: "sans-serif",
        isBorder: true,
        border: 8,
        borderColor: "#ffffff",
        blur: 8,
        borderStyle: "solid",
        popupPosition: {
          desktop: "bottom right",
          mobile: "bottom right",
        },
        isShadow: false,
        shadowX: 0,
        shadowY: 0,
        shadowColor: "#e1e0e0",
        animationDelay: 1,
        animationRepeat: 1,
        animationSpeed: 0.7,
        window: {
          padding: { desktop: 15, mobile: 15 },
        },
      },
      step_name: "Trigger",
      isStepHidden: true,
      zIndex: 2147483647,
    },
    section: [buttonSection],
  };
}

/** Storefront trigger must not block theme cart / add-to-cart clicks. */
function ensureStaticCartTriggerStepClickThrough(step) {
  if (!isStaticCartDrawerTriggerStep(step)) return step;
  const popupSettings = step.popupSettings || {};
  const dynamicalOptions = popupSettings.dynamicalOptions || {};
  return {
    ...step,
    popupSettings: {
      ...popupSettings,
      dynamicalOptions: {
        ...dynamicalOptions,
        overlay_style: "None",
        isClickable: false,
      },
    },
  };
}

/**
 * Normalize drawer + trigger steps and platform flags (safe on every template/popup load).
 */
export function normalizeStaticCartDrawerPopup(popup) {
  if (!popup) return popup;

  const typedAsCart =
    (popup.popUpType || popup.template_type) === CUSTOM_CART_TEMPLATE_TYPE ||
    isPopupLayout(popup, POPUP_LAYOUT_IDS.CART_DRAWER) ||
    popup.generalSettings?.isStaticCartDrawer === true;

  if (!typedAsCart && !isStaticCustomCartDrawer(popup)) {
    return popup;
  }

  const schema = Array.isArray(popup.schema)
    ? popup.schema.map((step) => {
        if (isStaticCartDrawerContentStep(step)) {
          return syncStaticCartDrawerSchemaStep(step);
        }
        return ensureStaticCartTriggerStepClickThrough(step);
      })
    : popup.schema;

  const { drawerStepIndex } = getStaticCartDrawerSteps({
    ...popup,
    schema,
  });

  let nextSchema = schema;
  if (
    typedAsCart &&
    drawerStepIndex == null &&
    (!Array.isArray(schema) || schema.length === 0)
  ) {
    nextSchema = [
      buildDefaultCartTriggerStep(1),
      ensureStaticCartDrawerSchemaStep({
        cartDrawerLayout: CUSTOM_CART_DRAWER_LAYOUT,
        section: buildStaticCartDrawerSections(),
      }),
    ];
  }

  return ensureStaticCartDrawerPopup({
    ...popup,
    popUpType: popup.popUpType || CUSTOM_CART_TEMPLATE_TYPE,
    schema: nextSchema,
  });
}

/**
 * Platform flags for static cart (does not mutate section payloads).
 */
export function ensureStaticCartDrawerPopup(popup) {
  if (!popup || !isStaticCustomCartDrawer(popup)) {
    return popup;
  }

  const productCondition = popup.generalSettings?.condition?.product || {};
  const mergedProductCondition = {
    products_to_offer: { type: "shopify_recommended", value: [] },
    allowMultipleProducts: true,
    products_to_offer_limit: 3,
    allowCustomerCartDrawer: true,
    linkToProduct: true,
    linkToProductOpenInNewTab: true,
    onCartAdd: false,
    onCartAddEventPrevented: false,
    ...productCondition,
  };
  if (mergedProductCondition.allowMultipleProducts == null) {
    mergedProductCondition.allowMultipleProducts = true;
  }
  if (
    !Number.isFinite(Number(mergedProductCondition.products_to_offer_limit)) ||
    Number(mergedProductCondition.products_to_offer_limit) < 1
  ) {
    mergedProductCondition.products_to_offer_limit = 3;
  }
  if (!mergedProductCondition.products_to_offer?.type) {
    mergedProductCondition.products_to_offer = {
      type: "specific_product",
      value: [],
    };
  }

  return {
    ...popup,
    popUpType: popup.popUpType || CUSTOM_CART_TEMPLATE_TYPE,
    generalSettings: {
      ...popup.generalSettings,
      hasProduct: popup.generalSettings?.hasProduct ?? true,
      isStaticCartDrawer: true,
      isDraggable: false,
      lockDraggable: popup.generalSettings?.lockDraggable ?? true,
      popupLayoutId:
        popup.generalSettings?.popupLayoutId || POPUP_LAYOUT_IDS.CART_DRAWER,
      staticCartSelectedPart:
        popup.generalSettings?.staticCartSelectedPart ??
        STATIC_CART_SECTION_INDEX.HEADER_TITLE,
      condition: {
        ...(popup.generalSettings?.condition || {}),
        product: mergedProductCondition,
      },
    },
  };
}

export function getStaticCartDrawerSections(popup, stepIndex) {
  const { drawerStepIndex } = getStaticCartDrawerSteps(popup);
  const resolvedIndex =
    stepIndex ?? (drawerStepIndex != null ? drawerStepIndex : 0);
  const step = Array.isArray(popup?.schema) ? popup.schema[resolvedIndex] : null;
  if (!step) {
    return [];
  }
  const layout = mergeLayout(step.cartDrawerLayout);
  const rawSection = Array.isArray(step.section) ? step.section : null;
  const rawSections = Array.isArray(step.sections) ? step.sections : null;
  // Prefer the array that already has hydrated offer products when dual keys diverge.
  let raw = rawSection ?? rawSections ?? [];
  if (rawSection && rawSections && rawSection !== rawSections) {
    const hasReadyProducts = (list) =>
      list.some(
        (sec) =>
          sec?.type === "PRODUCT" &&
          Array.isArray(sec.products) &&
          sec.products.length > 0
      );
    if (!hasReadyProducts(rawSection) && hasReadyProducts(rawSections)) {
      raw = rawSections;
    } else {
      raw = rawSection;
    }
  }
  return migrateStaticCartDrawerSections(raw, layout);
}

export function syncCartDrawerLayoutFromSections(step, layoutBase = CUSTOM_CART_DRAWER_LAYOUT) {
  const sections = step?.section ?? step?.sections ?? [];
  const layout = mergeLayout(step?.cartDrawerLayout || layoutBase);

  const titleSection = sections[STATIC_CART_SECTION_INDEX.HEADER_TITLE];
  const bodySection = sections[STATIC_CART_SECTION_INDEX.BODY];
  const discountCodeSection =
    sections[STATIC_CART_SECTION_INDEX.DISCOUNT_CODE_INPUT];
  const orderNoteSection = sections[STATIC_CART_SECTION_INDEX.ORDER_NOTE];
  const discountSummarySection =
    sections[STATIC_CART_SECTION_INDEX.FOOTER_DISCOUNT];
  const subtotalLabelSection =
    sections[STATIC_CART_SECTION_INDEX.FOOTER_SUBTOTAL_LABEL];
  const taxNoteSection = sections[STATIC_CART_SECTION_INDEX.FOOTER_TAX_NOTE];
  const checkoutSection = sections[STATIC_CART_SECTION_INDEX.FOOTER_CHECKOUT];

  if (titleSection?.text) {
    layout.header = { ...layout.header, cartTitle: titleSection.text };
  }
  if (bodySection?.setting) {
    const lineBorderColor =
      bodySection.setting.lineDividerColor ??
      bodySection.setting.lineItemBorderColor;
    layout.body = {
      ...layout.body,
      emptyMessage:
        step?.cartDrawerLayout?.body?.emptyMessage ?? layout.body?.emptyMessage,
      showRemoveButton: bodySection.setting.hideDeleteButton !== true,
      showQuantityControls: bodySection.setting.hideQuantityButton !== true,
      ...(lineBorderColor != null && String(lineBorderColor).trim() !== ""
        ? { lineItemBorderColor: lineBorderColor }
        : {}),
    };
  }
  if (discountCodeSection?.text) {
    layout.body = {
      ...layout.body,
      discountCodeTitle: discountCodeSection.text,
    };
  }
  if (discountCodeSection?.setting?.inputPlaceholder) {
    layout.body = {
      ...layout.body,
      discountCodePlaceholder: discountCodeSection.setting.inputPlaceholder,
    };
  }
  if (discountCodeSection?.setting?.saveButtonLabel) {
    layout.body = {
      ...layout.body,
      discountSaveLabel: discountCodeSection.setting.saveButtonLabel,
    };
  }
  if (orderNoteSection?.text) {
    layout.body = {
      ...layout.body,
      orderNoteTitle: orderNoteSection.text,
    };
  }
  if (orderNoteSection?.setting?.inputPlaceholder) {
    layout.body = {
      ...layout.body,
      orderNotePlaceholder: orderNoteSection.setting.inputPlaceholder,
    };
  }
  if (orderNoteSection?.setting?.saveButtonLabel) {
    layout.body = {
      ...layout.body,
      orderNoteSaveLabel: orderNoteSection.setting.saveButtonLabel,
    };
  }
  // Keep Total MRP / bill labels aligned across discount settings + footer layout.
  const discountSetting =
    discountSummarySection?.setting &&
    typeof discountSummarySection.setting === "object"
      ? discountSummarySection.setting
      : {};
  const totalMrpCandidates = [
    discountSetting.billSubtotalLabel,
    discountSetting.totalMrpLabel,
    layout.footer?.billSubtotalLabel,
    layout.footer?.totalMrpLabel,
    subtotalLabelSection?.text,
    layout.footer?.subtotalLabel,
  ];
  let totalMrpLabel = "Total MRP";
  for (const raw of totalMrpCandidates) {
    const label = String(raw ?? "").trim();
    if (
      !label ||
      /^subtotal$/i.test(label) ||
      /^estimated total$/i.test(label)
    ) {
      continue;
    }
    totalMrpLabel = label;
    break;
  }
  layout.footer = {
    ...layout.footer,
    billSubtotalLabel: totalMrpLabel,
    totalMrpLabel,
    subtotalLabel: totalMrpLabel,
    ...(typeof discountSetting.billSummaryTitle === "string"
      ? { billSummaryTitle: discountSetting.billSummaryTitle }
      : {}),
    ...(typeof discountSetting.deliveryFeeLabel === "string"
      ? { deliveryFeeLabel: discountSetting.deliveryFeeLabel }
      : {}),
    ...(typeof discountSetting.deliveryFeeText === "string"
      ? { deliveryFeeText: discountSetting.deliveryFeeText }
      : {}),
    ...(typeof discountSetting.mrpDiscountLabel === "string"
      ? { mrpDiscountLabel: discountSetting.mrpDiscountLabel }
      : {}),
    ...(typeof discountSetting.couponDiscountLabel === "string"
      ? { couponDiscountLabel: discountSetting.couponDiscountLabel }
      : {}),
    ...(typeof discountSetting.grandTotalLabel === "string"
      ? { grandTotalLabel: discountSetting.grandTotalLabel }
      : {}),
    ...(typeof discountSetting.sellingSubtotalLabel === "string"
      ? { sellingSubtotalLabel: discountSetting.sellingSubtotalLabel }
      : {}),
  };
  if (subtotalLabelSection && typeof subtotalLabelSection === "object") {
    subtotalLabelSection.text = totalMrpLabel;
  }
  if (taxNoteSection?.text) {
    layout.footer = {
      ...layout.footer,
      taxShippingNote: taxNoteSection.text,
    };
  }
  if (checkoutSection?.text) {
    layout.footer = { ...layout.footer, checkoutLabel: checkoutSection.text };
  }

  return layout;
}

export function buildCustomCartDrawerTemplate(overrides = {}) {
  const layout = mergeLayout(overrides.cartDrawerLayout);
  const sections = buildStaticCartDrawerSections(layout);

  return {
    popUpType: CUSTOM_CART_TEMPLATE_TYPE,
    is_active: true,
    ...overrides,
    generalSettings: {
      isDraggable: false,
      isStaticCartDrawer: true,
      hasProduct: true,
      title: "Custom Cart Drawer",
      staticCartSelectedPart: STATIC_CART_SECTION_INDEX.HEADER_TITLE,
      condition: {
        trigger: { step: 0 },
        embed: { is_embedded: false, step: 0 },
        product: {
          ...DEFAULT_PRODUCT_CONDITION,
          products_to_offer: { type: "specific_product", value: [] },
          allowMultipleProducts: true,
          products_to_offer_limit: 4,
          ...(overrides.generalSettings?.condition?.product || {}),
        },
        ...(overrides.generalSettings?.condition || {}),
      },
      cartDrawerUpsellShell: PRODUCT_SHELL_LAYOUT.CART_DRAWER_HORIZONTAL,
      ...(overrides.generalSettings || {}),
      isStaticCartDrawer: true,
      isDraggable: false,
    },
    schema: [
      buildDefaultCartTriggerStep(1),
      {
        activeStep: 1,
        cartDrawerLayout: layout,
        popupSettings: {
          ...CUSTOM_CART_DRAWER_POPUP_SETTINGS,
          step_name: "Drawer",
          ...(overrides.schema?.[1]?.popupSettings ||
            overrides.schema?.[0]?.popupSettings ||
            {}),
        },
        section: sections,
      },
    ],
  };
}

export function getStaticCartDrawerSteps(popup) {
  const schema = Array.isArray(popup?.schema) ? popup.schema : [];
  let triggerStepIndex = null;
  let drawerStepIndex = null;

  schema.forEach((step, index) => {
    if (isStaticCartDrawerContentStep(step)) {
      if (drawerStepIndex == null) drawerStepIndex = index;
    } else if (isStaticCartDrawerTriggerStep(step)) {
      if (triggerStepIndex == null) triggerStepIndex = index;
    }
  });

  if (drawerStepIndex == null && schema.length === 1) {
    drawerStepIndex = isStaticCartDrawerContentStep(schema[0]) ? 0 : null;
  }

  const triggerStep =
    triggerStepIndex != null ? schema[triggerStepIndex] : null;
  const hasTrigger =
    triggerStepIndex != null &&
    drawerStepIndex != null &&
    triggerStepIndex !== drawerStepIndex &&
    !isStepHidden(triggerStep);

  return { triggerStepIndex, drawerStepIndex, hasTrigger };
}

/** Editor sidebar: which schema step to list when the user selects a step chip. */
export function resolveStaticCartDrawerEditorSchemaStepIndex(popup, activeStep) {
  const step = popup?.schema?.[activeStep];
  if (isStaticCartDrawerContentStep(step)) return activeStep;
  if (isStaticCartDrawerTriggerStep(step)) return activeStep;
  const { drawerStepIndex } = getStaticCartDrawerSteps(popup);
  return drawerStepIndex ?? activeStep;
}

export function getCustomCartDrawerLayout(popup) {
  const schema = Array.isArray(popup?.schema) ? popup.schema : [];
  const { drawerStepIndex } = getStaticCartDrawerSteps(popup);
  const step =
    drawerStepIndex != null ? schema[drawerStepIndex] : schema[0] ?? null;
  if (!step) return CUSTOM_CART_DRAWER_LAYOUT;
  return syncCartDrawerLayoutFromSections(step);
}

export function isStaticCustomCartDrawer(popup) {
  if (!popup) return false;
  if (popup.generalSettings?.isStaticCartDrawer === true) return true;
  if (isPopupLayout(popup, POPUP_LAYOUT_IDS.CART_DRAWER)) return true;

  const type = popup.popUpType || popup.template_type;
  if (type !== CUSTOM_CART_TEMPLATE_TYPE) return false;

  const schema = Array.isArray(popup?.schema) ? popup.schema : [];
  if (schema.some((step) => isStaticCartDrawerContentStep(step))) return true;
  const layout = stepLayout(popup);
  return Boolean(layout?.header && layout?.body && layout?.footer);
}

/** Merchant theme host for inject-only cart upsell (keep spelling — themes paste this id). */
export const THEME_CART_UPSELL_HOST_ID = "weupsell-cart-drwer-upsell";

/** Default true: replace theme cart with WeUpsell custom drawer. */
export function isCustomCartDrawerThemeReplaceEnabled(popup) {
  return (
    popup?.generalSettings?.condition?.product?.allowCustomerCartDrawer !==
    false
  );
}

/** When replace is off: mount upsell strip into #weupsell-cart-drwer-upsell only. */
export function isCustomCartDrawerThemeUpsellInjectMode(popup) {
  return (
    isStaticCustomCartDrawer(popup) &&
    !isCustomCartDrawerThemeReplaceEnabled(popup)
  );
}

/** Canonical default_schema for Mongo seed / exports (Trigger + Drawer, 8 sections). */
export function buildCustomCartDrawerDefaultSchema() {
  const drawerStep = ensureStaticCartDrawerSchemaStep({
    cartDrawerLayout: CUSTOM_CART_DRAWER_LAYOUT,
    section: buildStaticCartDrawerSections(),
  });

  return ensureStaticCartDrawerPopup({
    popUpType: CUSTOM_CART_TEMPLATE_TYPE,
    generalSettings: {
      title: "Custom Cart Drawer",
      isStaticCartDrawer: true,
      isDraggable: false,
      lockDraggable: true,
      popupLayoutId: POPUP_LAYOUT_IDS.CART_DRAWER,
      staticCartSelectedPart: STATIC_CART_SECTION_INDEX.HEADER_TITLE,
      condition: {
        product: {
          allowCustomerCartDrawer: true,
          onCartAdd: false,
          onCartAddEventPrevented: false,
        },
      },
    },
    schema: [buildDefaultCartTriggerStep(1), drawerStep],
  });
}

function stepLayout(popup) {
  const schema = Array.isArray(popup?.schema) ? popup.schema : [];
  const { drawerStepIndex } = getStaticCartDrawerSteps(popup);
  const step =
    drawerStepIndex != null ? schema[drawerStepIndex] : schema[0] ?? null;
  return step?.cartDrawerLayout;
}
